import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useMessageStore } from '../stores/messageStore';

interface Props {
  targetUserId: string;
  jobPostingId?: string | null;
  /**
   * 표시 변형. compact는 아이콘+짧은 텍스트, full은 큰 버튼
   */
  variant?: 'compact' | 'full';
  className?: string;
}

/**
 * employer가 구직자/지원자에게 DM을 시작하는 버튼.
 * employer/admin 외에는 렌더하지 않음.
 */
export default function StartChatButton({
  targetUserId,
  jobPostingId = null,
  variant = 'compact',
  className = '',
}: Props) {
  const user = useAuthStore((s) => s.user);
  const startConversation = useMessageStore((s) => s.startConversation);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!user) return null;
  if (user.role !== 'employer' && user.role !== 'admin') return null;
  if (user.id === targetUserId) return null;

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const conv = await startConversation({
        userId: targetUserId,
        jobPostingId: jobPostingId || undefined,
      });
      navigate(`/chat/${conv.id}`);
    } catch (err: any) {
      alert(err.message || '대화를 시작할 수 없습니다.');
    } finally {
      setBusy(false);
    }
  };

  if (variant === 'full') {
    return (
      <button
        onClick={handleClick}
        disabled={busy}
        className={`w-full py-3 bg-accent text-white rounded-lg font-semibold hover:bg-accent/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${className}`}
      >
        <MessageCircle className="w-5 h-5" />
        {busy ? '대화 준비 중...' : '메시지 보내기'}
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={busy}
      className={`flex items-center gap-1 px-3 py-1.5 text-sm bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50 ${className}`}
    >
      <MessageCircle className="w-3.5 h-3.5" />
      {busy ? '...' : '메시지'}
    </button>
  );
}
